/**
 * Encrypt or decrypt a string with the given XOR key.
 *
 * @name xorCrypt
 * @param {string} value - The string to encrypt.
 * @param {int} [key=6] - The XOR key to use when encrypting.
 *
 * @return The resulting XOR'ed string.
 */
export function xorCrypt(value: string, key = 6): string {
  return value.split("").reduce((output, char) => {
    output += String.fromCharCode(key ^ char.charCodeAt(0));
    return output;
  }, "");
}

/**
 * Check if a string is likely XOR encrypted.
 *
 * @param {string} value - The string to check.
 * @param {int} [key=6] - The XOR key to use for decryption.
 *
 * @return {boolean} True if the string is likely XOR encrypted, false otherwise.
 */
export function isXorEncrypted(value: string, key = 6): boolean {
  const decrypted = xorCrypt(value, key);

  const printableChars = decrypted.split("").filter((char) => {
    const charCode = char.charCodeAt(0);
    return charCode >= 32 && charCode <= 126;
  }).length;

  if (printableChars === 0) return false;

  // Heuristic: if less than 70% of the characters are printable, consider it encrypted
  return printableChars / decrypted.length < 0.7;
}
